import { useCallback, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { GuestBanner } from "@/components/owner/GuestBanner";
import { useLenis } from "@/hooks/useLenis";
import { resolveServices } from "@/data/services";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { Manifesto } from "@/components/Manifesto";
import { Services } from "@/components/Services";
import { Itinerary } from "@/components/Itinerary";
import { ConciergeDrawer } from "@/components/ConciergeDrawer";
import { Footer } from "@/components/Footer";
import OwnerPage from "@/pages/OwnerPage";
import OwnerInvoices from "@/pages/OwnerInvoices";
import OwnerVat from "@/pages/OwnerVat";
import PublicInvoice from "@/pages/PublicInvoice";
import SharedItinerary from "@/pages/SharedItinerary";
import DriverView from "@/pages/DriverView";
import TripsPage from "@/pages/TripsPage";
import TripReview from "@/pages/TripReview";
import GuestPage from "@/pages/GuestPage";
import TripAdminPage from "@/pages/TripAdminPage";
import TourPage from "@/pages/TourPage";
import OwnerCalendar from "@/pages/OwnerCalendar";
import OwnerCatalogue from "@/pages/OwnerCatalogue";
import OwnerDebts from "@/pages/OwnerDebts";
import BudgetPage from "@/pages/BudgetPage";
import { Tours } from "@/components/Tours";

const STORAGE_KEY = "exclusive-reykjavik-list";

function Home() {
  useLenis();
  const [selected, setSelected] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    } catch {
      return [];
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [country, setCountry] = useState(() => localStorage.getItem("er-country") || "Iceland");
  const [localOptions, setLocalOptions] = useState({ known: false, options: [] });

  useEffect(() => {
    localStorage.setItem("er-country", country);
  }, [country]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selected));
  }, [selected]);

  const toggle = useCallback((id) => {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  }, []);

  const selectedServices = resolveServices(selected, localOptions.options || []);

  return (
    <div className="min-h-screen overflow-x-clip bg-[#070709] text-neutral-100">
      <div className="noise-overlay" />
      <Hero onOpenDrawer={() => setDrawerOpen(true)} />
      <Marquee />
      <Manifesto />
      <Tours />
      <Services
        selected={selected}
        onToggle={toggle}
        country={country}
        onCountryChange={setCountry}
        localOptions={localOptions}
        onLocalOptions={setLocalOptions}
      />
      <Itinerary selectedServices={selectedServices} country={country} />
      <Footer />
      <ConciergeDrawer
        open={drawerOpen}
        onOpen={() => setDrawerOpen(true)}
        onClose={() => setDrawerOpen(false)}
        selectedServices={selectedServices}
        onToggle={toggle}
        onClear={() => setSelected([])}
        country={country}
      />
    </div>
  );
}

// every route change starts at the top of the new page
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <GuestBanner />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/owner" element={<OwnerPage />} />
        <Route path="/owner/trips" element={<TripsPage />} />
        <Route path="/trip/:token" element={<TripReview />} />
        <Route path="/trip/:token/g/:guestToken" element={<GuestPage />} />
        <Route path="/trip-admin/:adminToken" element={<TripAdminPage />} />
        <Route path="/tour/:publicToken" element={<TourPage />} />
        <Route path="/invoice/:token" element={<PublicInvoice />} />
        <Route path="/owner/catalogue" element={<OwnerCatalogue />} />
        <Route path="/owner/debts" element={<OwnerDebts />} />
        <Route path="/owner/invoices" element={<OwnerInvoices />} />
        <Route path="/owner/vat" element={<OwnerVat />} />
        <Route path="/owner/calendar" element={<OwnerCalendar />} />
        <Route path="/budget" element={<BudgetPage />} />
        <Route path="/i/:id" element={<SharedItinerary />} />
        <Route path="/drive/:token" element={<DriverView />} />
      </Routes>
      <Toaster
        position="top-center"
        theme="dark"
        toastOptions={{
          style: {
            background: "#0c0c10",
            border: "1px solid rgba(212,175,55,0.35)",
            color: "#e8d5b7",
          },
        }}
      />
    </BrowserRouter>
  );
}
